/**
 * Hours & recording: where the business is, when each place is open, the days it is closed,
 * and whether calls are kept as audio.
 *
 * TOGETHER BECAUSE SETTINGS V2 PUT THEM TOGETHER, and the pairing holds up: both are facts
 * the agent acts on at the moment it answers - inside or outside hours, recording or not -
 * where everything on Details is about the dashboard.
 *
 * A READ-ONLY CARD PER LOCATION, AND A DIALOG TO CHANGE IT. The week is something an owner
 * reads far more often than they edit, and seven rows of switches and selects is a form
 * that shouts. The card is the week as it would be printed on the door - today marked, with
 * whether it is open right now - and Edit and Closures each open a dialog. Their Done writes
 * into the draft like any other pane, so the one save bar still saves.
 *
 * MINUTES AND BREAKS, NOT WHOLE HOURS. Up to three periods a day, in quarter hours from the
 * selects (the Worker takes any minute; a site that says 08:10 keeps it). The heatmap counts
 * an hour open if any part of it is, which is the Worker's rule, not this file's.
 *
 * OPEN MEANS ANY LOCATION. Tools set to "during opening hours" and the after-hours share ask
 * whether anybody is there; each card's dot says whether that one place is.
 */

import { useState, type ReactNode } from "react";
import type { Closure, DayKey, Location, Period, Week } from "../../../api";
import { Button, IconButton, SettingCard, Select, Switch, TextInput, Toggle, cx } from "../../../ui";
import { Dialog } from "../../Dialog";
import { Alert, CalendarX, ChevronRight, Clock, Close, Plus, Trash } from "../../Icons";
import { useClock } from "../../useClock";
import { useDraft } from "../draft";
import {
  MAX_LOCATIONS,
  MAX_PERIODS,
  QUARTERS,
  WEEK,
  closedWeek,
  closureOn,
  closureRange,
  closuresSummary,
  dayProblem,
  newId,
  nextPeriod,
  officeWeek,
  openAt,
  toMinutes,
  upcoming,
  wallClock,
  type WallClock,
} from "../hours";

type Open = { kind: "edit"; id: string } | { kind: "new" } | { kind: "closures"; id: string } | null;

export function HoursPane() {
  const { state, set } = useDraft();
  const { locations, recordAudio } = state.draft.hours;
  const clock = wallClock(useClock(), state.draft.business.timezone);
  const [open, setOpen] = useState<Open>(null);

  const save = (next: Location[]) => set("hours", { locations: next, recordAudio });
  const editing = open && open.kind !== "new" ? locations.find((l) => l.id === open.id) : undefined;

  return (
    <>
      {locations.length ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,340px),1fr))] items-start gap-4">
          {locations.map((location) => (
            <LocationCard
              key={location.id}
              location={location}
              clock={clock}
              onEdit={() => setOpen({ kind: "edit", id: location.id })}
              onClosures={() => setOpen({ kind: "closures", id: location.id })}
            />
          ))}
        </div>
      ) : (
        <EmptyCard onStart={() => setOpen({ kind: "new" })} />
      )}

      {locations.length > 0 && locations.length < MAX_LOCATIONS && (
        <div>
          <Button type="button" onClick={() => setOpen({ kind: "new" })}>
            <Plus size={16} />
            Add location
          </Button>
        </div>
      )}

      {/* A PRIVACY CONTROL AND NOT A PREFERENCE, which is why it has a sentence where the
          cards above have none. It applies to calls from here on - switching it off does
          not delete recordings already made - and it takes effect on the next call. */}
      <SettingCard className="flex flex-wrap items-center justify-between gap-6 px-6 py-5">
        <div className="grid max-w-[560px] gap-1">
          <span className="text-base font-semibold text-ink">Record calls</span>
          <span className="text-xs text-ink-faint">
            Keep audio of each call so you can listen back. Transcripts are kept either way. Switching it off keeps the recordings you already have.
          </span>
        </div>
        <span className="flex items-center gap-2.5 text-sm font-medium text-ink">
          {recordAudio ? "On" : "Off"}
          <Switch checked={recordAudio} label="Record calls" onChange={(next) => set("hours", { locations, recordAudio: next })} />
        </span>
      </SettingCard>

      {open?.kind === "new" && (
        <LocationDialog
          initial={{
            id: newId("loc"),
            // The first one is the main office; after that the owner names it.
            name: locations.length ? "" : "Main office",
            address: "",
            hours: officeWeek(),
            closures: [],
          }}
          isNew
          taken={locations.map((l) => l.name)}
          onDone={(location) => {
            save([...locations, location]);
            setOpen(null);
          }}
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === "edit" && editing && (
        <LocationDialog
          initial={editing}
          taken={locations.filter((l) => l.id !== editing.id).map((l) => l.name)}
          onDone={(location) => {
            save(locations.map((l) => (l.id === location.id ? location : l)));
            setOpen(null);
          }}
          onRemove={() => {
            save(locations.filter((l) => l.id !== editing.id));
            setOpen(null);
          }}
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === "closures" && editing && (
        <ClosuresDialog
          location={editing}
          today={clock.date}
          others={locations.length - 1}
          onDone={(closures, toAll) => {
            save(
              locations.map((l) =>
                l.id === editing.id
                  ? { ...l, closures }
                  : { ...l, closures: [...l.closures, ...toAll.map((c) => ({ ...c, id: newId("cls") }))] },
              ),
            );
            setOpen(null);
          }}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

/* --- the card ------------------------------------------------------------------------------ */

function CardHeader({ subtitle, aside }: { subtitle: string; aside?: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-line-soft bg-ground px-5 py-4">
      <div className="grid min-w-0 gap-1">
        <h3 className="flex items-center gap-2 text-base font-semibold text-ink">
          <Clock size={18} className="flex-none" />
          Opening hours
        </h3>
        <p className="truncate text-sm text-ink-faint">{subtitle}</p>
      </div>
      {aside}
    </div>
  );
}

function LocationCard({
  location,
  clock,
  onEdit,
  onClosures,
}: {
  location: Location;
  clock: WallClock;
  onEdit: () => void;
  onClosures: () => void;
}) {
  const closedToday = closureOn(location, clock.date);
  const openNow = openAt(location, clock);

  return (
    <SettingCard flush>
      <CardHeader
        subtitle={location.address ? `${location.name} · ${location.address}` : location.name}
        aside={
          <Button type="button" variant="ghost" className="-mr-2 -mt-1" onClick={onEdit} aria-label={`Edit ${location.name}`}>
            Edit
          </Button>
        }
      />
      <ul className="grid gap-0.5 p-3">
        {WEEK.map(([key, label]) => {
          const today = key === clock.day;
          const periods = location.hours[key];
          return (
            <li
              key={key}
              className={cx("flex items-start justify-between gap-4 rounded-md px-3 py-2 text-base", today && "bg-ground")}
              aria-current={today ? "date" : undefined}
            >
              <span className={today ? "font-medium text-ink" : "text-ink-faint"}>{label}</span>
              <span className="flex items-center gap-2.5">
                {today && (
                  <>
                    {/* The one bit of colour on the card, and it is a fact: open or shut, now. */}
                    <span
                      aria-hidden="true"
                      title={openNow ? "Open now" : "Closed now"}
                      className={cx("size-2 flex-none rounded-full", openNow ? "bg-good" : "bg-alert")}
                    />
                    <span className="sr-only">{openNow ? "Open now" : "Closed now"}</span>
                  </>
                )}
                {today && closedToday ? (
                  <span className="text-ink-faint">Closed{closedToday.note ? ` · ${closedToday.note}` : ""}</span>
                ) : periods.length ? (
                  <span className="grid justify-items-end text-ink tabular-nums">
                    {periods.map(([from, to]) => (
                      <span key={from}>
                        {from} – {to}
                      </span>
                    ))}
                  </span>
                ) : (
                  <span className="text-ink-faint">Closed</span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        onClick={onClosures}
        className="flex w-full items-center gap-3 border-t border-line-soft px-5 py-3.5 text-left text-base text-ink-faint hover:bg-ground"
      >
        <CalendarX size={18} className="flex-none" />
        <span>Closures</span>
        <span className="ml-auto truncate">{closuresSummary(location.closures, clock.date)}</span>
        <ChevronRight size={16} className="flex-none" />
      </button>
    </SettingCard>
  );
}

function EmptyCard({ onStart }: { onStart: () => void }) {
  return (
    <SettingCard flush className="max-w-[420px]">
      <CardHeader subtitle="Not set yet" />
      <div className="grid justify-items-start gap-3 px-5 py-5">
        <p className="text-sm text-ink-soft">Your agent doesn't know when you're open, so it can't tell callers.</p>
        <Button type="button" variant="primary" onClick={onStart}>
          Set opening hours
        </Button>
      </div>
    </SettingCard>
  );
}

/* --- editing a location -------------------------------------------------------------------- */

function LocationDialog({
  initial,
  isNew,
  taken,
  onDone,
  onRemove,
  onClose,
}: {
  initial: Location;
  isNew?: boolean;
  /** The other locations' names, which this one may not repeat. */
  taken: string[];
  onDone: (location: Location) => void;
  onRemove?: () => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(initial.name);
  const [address, setAddress] = useState(initial.address);
  const [week, setWeek] = useState<Week>(initial.hours);
  // A CLOSED DAY KEEPS ITS TIMES HERE AND LOSES THEM FROM THE SCREEN. A Saturday switched off
  // and straight back on comes back as the 08:00 to 12:00 it was, not as 09:00 to 17:00.
  const [remembered, setRemembered] = useState<Week>(() => {
    const seed = { ...closedWeek() };
    for (const [key] of WEEK) seed[key] = initial.hours[key].length ? initial.hours[key] : [["09:00", "17:00"]];
    return seed;
  });
  const [confirmRemove, setConfirmRemove] = useState(false);

  const setDay = (key: DayKey, periods: Period[]) => {
    if (periods.length) setRemembered((previous) => ({ ...previous, [key]: periods }));
    setWeek((previous) => ({ ...previous, [key]: periods }));
  };

  const trimmed = name.trim();
  const nameProblem = !trimmed
    ? "Give this location a name"
    : taken.some((other) => other.trim().toLowerCase() === trimmed.toLowerCase())
      ? `Another location is already called "${trimmed}"`
      : null;
  const problems = WEEK.map(([key, label]) => dayProblem(label, week[key]));
  const blocked = Boolean(nameProblem) || problems.some(Boolean);

  return (
    <Dialog
      title={isNew ? "Add location" : `Edit ${initial.name}`}
      onClose={onClose}
      footer={
        <>
          <Button
            type="button"
            variant="primary"
            disabled={blocked}
            onClick={() => onDone({ ...initial, name: trimmed, address: address.trim(), hours: week })}
          >
            Done
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {onRemove && (
            <Button
              type="button"
              variant={confirmRemove ? "danger" : "ghost"}
              className="ml-auto"
              onClick={() => (confirmRemove ? onRemove() : setConfirmRemove(true))}
            >
              <Trash size={16} />
              {confirmRemove ? `Remove ${initial.name}?` : "Remove location"}
            </Button>
          )}
        </>
      }
    >
      <label className="grid gap-1.5">
        <span className="text-sm font-medium text-ink">Name</span>
        <TextInput
          autoFocus={isNew}
          className="w-full"
          value={name}
          maxLength={60}
          placeholder="e.g. Main office, Parramatta"
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      <label className="grid gap-1.5">
        <span className="text-sm font-medium text-ink">
          Address <span className="font-normal text-ink-faint">(optional)</span>
        </span>
        <TextInput
          className="w-full"
          value={address}
          maxLength={200}
          placeholder="Where callers can find you"
          onChange={(event) => setAddress(event.target.value)}
        />
      </label>

      <div className="grid gap-1">
        <span className="text-sm font-medium text-ink">Opening hours</span>
        <div className="grid">
          {WEEK.map(([key, label], index) => {
            const periods = week[key];
            const on = periods.length > 0;
            return (
              <div key={key} className={cx("grid gap-1.5 py-2.5", index > 0 && "border-t border-line-soft")}>
                <div className="grid grid-cols-[96px_auto_minmax(0,1fr)] items-start gap-3 max-[480px]:grid-cols-[minmax(0,1fr)_auto]">
                  <span className={cx("pt-1.5 text-sm font-medium", on ? "text-ink" : "text-ink-faint")}>{label}</span>
                  <span className="pt-1.5">
                    <Switch
                      checked={on}
                      label={`${label} open`}
                      onChange={(next) => setDay(key, next ? remembered[key] : [])}
                    />
                  </span>
                  {on ? (
                    <div className="grid justify-items-end gap-1.5 max-[480px]:col-span-2">
                      {periods.map((period, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <TimeSelect
                            value={period[0]}
                            label={`${label} opens${i ? ` (${i + 1})` : ""}`}
                            onChange={(time) => setDay(key, periods.map((p, j) => (j === i ? [time, p[1]] : p)))}
                          />
                          <span aria-hidden="true" className="text-sm text-ink-faint">
                            →
                          </span>
                          <TimeSelect
                            value={period[1]}
                            label={`${label} closes${i ? ` (${i + 1})` : ""}`}
                            onChange={(time) => setDay(key, periods.map((p, j) => (j === i ? [p[0], time] : p)))}
                          />
                          {i === 0 ? (
                            <IconButton
                              type="button"
                              title="Add a break"
                              disabled={periods.length >= MAX_PERIODS || toMinutes(periods[periods.length - 1]![1]) > 22 * 60}
                              className="disabled:invisible"
                              onClick={() => setDay(key, [...periods, nextPeriod(periods)])}
                            >
                              <Plus size={16} />
                              <span className="sr-only">Add opening times to {label}</span>
                            </IconButton>
                          ) : (
                            <IconButton type="button" title="Remove" onClick={() => setDay(key, periods.filter((_, j) => j !== i))}>
                              <Close size={16} />
                              <span className="sr-only">Remove these times from {label}</span>
                            </IconButton>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="justify-self-end pt-1.5 text-sm text-ink-faint max-[480px]:col-span-2">Closed</span>
                  )}
                </div>
                {problems[index] && <Problem>{problems[index]}</Problem>}
              </div>
            );
          })}
        </div>
      </div>
      {nameProblem && name !== initial.name && <Problem>{nameProblem}</Problem>}
    </Dialog>
  );
}

/** A native select of quarter hours, plus the stored time when it is not on a quarter. */
function TimeSelect({ value, label, onChange }: { value: string; label: string; onChange: (time: string) => void }) {
  const options = QUARTERS.includes(value) ? QUARTERS : [...QUARTERS, value].sort();
  return (
    <Select className="min-w-0 tabular-nums" value={value} aria-label={label} onChange={(event) => onChange(event.target.value)}>
      {options.map((time) => (
        <option key={time} value={time}>
          {time}
        </option>
      ))}
    </Select>
  );
}

function Problem({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="flex items-center gap-1.5 text-xs text-alert">
      <Alert size={14} className="flex-none" />
      {children}
    </p>
  );
}

/* --- closures ------------------------------------------------------------------------------ */

function ClosuresDialog({
  location,
  today,
  others,
  onDone,
  onClose,
}: {
  location: Location;
  today: string;
  /** How many other locations there are, for "All locations". */
  others: number;
  /** This location's closures, and the ones added to every other location too. */
  onDone: (closures: Closure[], toAll: Closure[]) => void;
  onClose: () => void;
}) {
  const [closures, setClosures] = useState<Closure[]>(() => upcoming(location.closures, today));
  const [toAll, setToAll] = useState<Closure[]>([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [everywhere, setEverywhere] = useState(false);

  const end = to || from;
  const problem = from && end < from ? "The last day is before the first" : from && end < today ? "That's already over" : null;

  const add = () => {
    if (!from || problem) return;
    const closure: Closure = { id: newId("cls"), from, to: end, note: note.trim() };
    setClosures((previous) => upcoming([...previous, closure], today));
    if (everywhere) setToAll((previous) => [...previous, closure]);
    setFrom("");
    setTo("");
    setNote("");
  };

  const remove = (id: string) => {
    setClosures((previous) => previous.filter((c) => c.id !== id));
    setToAll((previous) => previous.filter((c) => c.id !== id));
  };

  return (
    <Dialog
      title="Closures"
      meta={<span>{location.name}: days it is closed all day, like public holidays or a break.</span>}
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="primary" onClick={() => onDone(closures, toAll)}>
            Done
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      {closures.length ? (
        <ul className="grid rounded-md border border-line">
          {closures.map((closure, index) => (
            <li key={closure.id} className={cx("flex items-center gap-3 px-3 py-2.5", index > 0 && "border-t border-line-soft")}>
              <CalendarX size={16} className="flex-none text-ink-faint" />
              <span className="min-w-0 flex-1 text-sm">
                <span className="text-ink">{closureRange(closure, today, true)}</span>
                {closure.note && <span className="text-ink-faint"> · {closure.note}</span>}
              </span>
              <IconButton type="button" title="Remove" onClick={() => remove(closure.id)}>
                <Close size={16} />
                <span className="sr-only">Remove the closure on {closureRange(closure, today, true)}</span>
              </IconButton>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-ink-faint">None scheduled.</p>
      )}

      <div className="grid gap-3 rounded-md bg-ground p-3.5">
        <span className="text-sm font-medium text-ink">Add a closure</span>
        <div className="grid grid-cols-2 gap-3">
          <label className="grid gap-1">
            <span className="text-xs text-ink-faint">From</span>
            <TextInput type="date" className="w-full" min={today} value={from} onChange={(event) => setFrom(event.target.value)} />
          </label>
          <label className="grid gap-1">
            <span className="text-xs text-ink-faint">
              To <span className="text-ink-ghost">(optional)</span>
            </span>
            <TextInput type="date" className="w-full" min={from || today} value={to} onChange={(event) => setTo(event.target.value)} />
          </label>
        </div>
        <label className="grid gap-1">
          <span className="text-xs text-ink-faint">Note</span>
          <TextInput className="w-full" value={note} maxLength={80} placeholder="e.g. Christmas" onChange={(event) => setNote(event.target.value)} />
        </label>
        {problem && <Problem>{problem}</Problem>}
        <div className="flex flex-wrap items-center justify-between gap-3">
          {others > 0 ? (
            <Toggle checked={everywhere} onChange={setEverywhere}>
              All locations
            </Toggle>
          ) : (
            <span />
          )}
          <Button type="button" disabled={!from || Boolean(problem)} onClick={add}>
            <Plus size={16} />
            Add closure
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
